import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  pickHolder,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind, holderClues, playersWithRole } from './util.ts';

/**
 * Safe (GDD §8.2): a Mastermind-style combination. The owner's profile (on the mastermind's
 * screen) gives true statements about the code; each wrong attempt answers with clicks
 * (right digit, right place) and hums (right digit, wrong place).
 */

export type Constraint =
  | { k: 'digitAt'; pos: number; d: number }
  | { k: 'contains'; d: number }
  | { k: 'notContains'; d: number }
  | { k: 'sum'; s: number }
  | { k: 'parity'; pos: number; even: boolean }
  | { k: 'greater'; a: number; b: number };

export interface SafeParams {
  digits: number;
  repeats: boolean;
  maxAttempts: number;
  code: number[];
  constraints: Constraint[];
}

export interface Attempt {
  digits: number[];
  clicks: number;
  hums: number;
}

export interface SafeState {
  history: Attempt[];
  attemptsLeft: number;
  lockedUntilMs: number;
}

const DIGITS_BY_LEVEL = [3, 3, 3, 4, 4, 5] as const;
const ATTEMPTS_BY_LEVEL = [10, 10, 9, 9, 8, 8] as const;
const MAX_CANDIDATES_BY_LEVEL = [12, 12, 30, 60, 120, 200] as const;

const params = (p: PuzzleInstance) => p.params as SafeParams;
const state = (p: PuzzleInstance) => p.state as SafeState;

export function evalConstraint(c: Constraint, code: readonly number[]): boolean {
  switch (c.k) {
    case 'digitAt':
      return code[c.pos] === c.d;
    case 'contains':
      return code.includes(c.d);
    case 'notContains':
      return !code.includes(c.d);
    case 'sum':
      return code.reduce((a, b) => a + b, 0) === c.s;
    case 'parity':
      return ((code[c.pos] as number) % 2 === 0) === c.even;
    case 'greater':
      return (code[c.a] as number) > (code[c.b] as number);
  }
}

/** Mastermind feedback: clicks = right place, hums = right digit elsewhere (multiset-aware). */
export function feedback(
  code: readonly number[],
  guess: readonly number[],
): { clicks: number; hums: number } {
  let clicks = 0;
  const codeLeft: number[] = new Array<number>(10).fill(0);
  const guessLeft: number[] = new Array<number>(10).fill(0);
  for (let i = 0; i < code.length; i++) {
    const c = code[i] as number;
    const g = guess[i] as number;
    if (c === g) clicks++;
    else {
      codeLeft[c] = (codeLeft[c] ?? 0) + 1;
      guessLeft[g] = (guessLeft[g] ?? 0) + 1;
    }
  }
  let hums = 0;
  for (let d = 0; d < 10; d++) hums += Math.min(codeLeft[d] ?? 0, guessLeft[d] ?? 0);
  return { clicks, hums };
}

function allDistinct(code: readonly number[]): boolean {
  return new Set(code).size === code.length;
}

/** Codes consistent with the constraints, up to `limit + 1` (so callers can tell "too many"). */
export function listCandidates(
  digits: number,
  repeats: boolean,
  constraints: readonly Constraint[],
  limit: number,
): number[][] {
  const out: number[][] = [];
  const code = new Array<number>(digits).fill(0);
  const total = 10 ** digits;
  for (let n = 0; n < total; n++) {
    let x = n;
    for (let i = digits - 1; i >= 0; i--) {
      code[i] = x % 10;
      x = Math.floor(x / 10);
    }
    if (!repeats && !allDistinct(code)) continue;
    let ok = true;
    for (const c of constraints) {
      if (!evalConstraint(c, code)) {
        ok = false;
        break;
      }
    }
    if (ok) {
      out.push(code.slice());
      if (out.length > limit) return out;
    }
  }
  return out;
}

/** Plays the "always guess a consistent code" strategy against the real code. */
export function simulateSolve(code: readonly number[], candidates: readonly number[][]): number {
  let pool = candidates;
  let attempts = 0;
  while (pool.length > 0) {
    const guess = pool[0] as number[];
    attempts++;
    const fb = feedback(code, guess);
    if (fb.clicks === code.length) return attempts;
    pool = pool.filter((c) => {
      const f = feedback(c, guess);
      return f.clicks === fb.clicks && f.hums === fb.hums;
    });
  }
  return Number.POSITIVE_INFINITY;
}

function trueConstraints(code: readonly number[], repeats: boolean, rng: Rng): Constraint[] {
  const n = code.length;
  const out: Constraint[] = [];
  for (let pos = 0; pos < n; pos++) {
    out.push({ k: 'digitAt', pos, d: code[pos] as number });
    out.push({ k: 'parity', pos, even: (code[pos] as number) % 2 === 0 });
  }
  for (const d of new Set(code)) out.push({ k: 'contains', d });
  for (let d = 0; d < 10; d++) if (!code.includes(d)) out.push({ k: 'notContains', d });
  out.push({ k: 'sum', s: code.reduce((a, b) => a + b, 0) });
  for (let a = 0; a < n; a++) {
    for (let b = 0; b < n; b++) {
      if (a !== b && (code[a] as number) > (code[b] as number)) out.push({ k: 'greater', a, b });
    }
  }
  void repeats;
  return rng.shuffle(out);
}

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const digits = DIGITS_BY_LEVEL[level] ?? 4;
  const repeats = level >= 3;
  const maxAttempts = ATTEMPTS_BY_LEVEL[level] ?? 9;
  const cap = MAX_CANDIDATES_BY_LEVEL[level] ?? 60;

  for (let attempt = 0; attempt < 40; attempt++) {
    const code = repeats
      ? Array.from({ length: digits }, () => rng.int(0, 9))
      : rng.shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, digits);
    const pool = trueConstraints(code, repeats, rng);
    const constraints: Constraint[] = [];
    let candidates: number[][] = [];
    for (const c of pool) {
      constraints.push(c);
      candidates = listCandidates(digits, repeats, constraints, cap);
      if (candidates.length <= cap) break;
    }
    if (candidates.length > cap) continue;
    // Prefer a profile with few lines: drop lines that do not matter.
    for (let i = constraints.length - 1; i >= 0; i--) {
      const without = constraints.filter((_, j) => j !== i);
      if (without.length === 0) continue;
      if (listCandidates(digits, repeats, without, cap).length <= cap) constraints.splice(i, 1);
    }
    candidates = listCandidates(digits, repeats, constraints, cap);
    if (simulateSolve(code, candidates) > maxAttempts - 1) continue;

    const p: SafeParams = { digits, repeats, maxAttempts, code, constraints };
    const s: SafeState = { history: [], attemptsLeft: maxAttempts, lockedUntilMs: 0 };
    return {
      params: p,
      state: s,
      clues: [
        {
          id: 'profile',
          kind: 'safeProfile',
          holder: pickHolder(ctx.roles, ['mastermind']),
          value: constraints,
        },
      ],
      solvers: ['locksmith'] as Role[],
    };
  }
  throw new Error('safe generator failed to produce a solvable puzzle');
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  if (act.kind !== 'safe.try') return failResult('invalid', 'not a safe action');
  const par = params(p);
  const st = state(p);
  if (ctx.actorRole !== 'locksmith')
    return failResult('notAllowed', 'only the locksmith cracks safes');
  if (
    act.digits.length !== par.digits ||
    act.digits.some((d) => !Number.isInteger(d) || d < 0 || d > 9)
  ) {
    return failResult('invalid', 'wrong number of digits');
  }
  if (ctx.execMs < st.lockedUntilMs) return failResult('cooldown', 'the safe is jammed');

  const fb = feedback(par.code, act.digits);
  const audience = playersWithRole(ctx.players, 'locksmith');
  const attempt: Attempt = { digits: act.digits.slice(), clicks: fb.clicks, hums: fb.hums };
  if (fb.clicks === par.digits) {
    st.history.push(attempt);
    return okResult({ solved: true, events: [{ type: 'safe.open', audience, data: {} }] });
  }
  const prevLeft = st.attemptsLeft;
  const prevLock = st.lockedUntilMs;
  st.history.push(attempt);
  st.attemptsLeft -= 1;
  let jammed = false;
  if (st.attemptsLeft <= 0) {
    st.lockedUntilMs = ctx.execMs + BALANCE.safeLockMs;
    st.attemptsLeft = BALANCE.safeRefillAttempts;
    jammed = true;
  }
  return okResult({
    errorTenths: BALANCE.penalty.safe,
    undo: { kind: 'popAttempt', data: { prevLeft, prevLock } },
    events: [
      {
        type: 'safe.feedback',
        audience,
        data: { clicks: fb.clicks, hums: fb.hums, jammed },
      },
    ],
  });
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const base: Record<string, unknown> = {
    type: 'safe',
    solved: p.solved,
    digits: par.digits,
    repeats: par.repeats,
    clues: holderClues(p, ctx.role),
  };
  if (ctx.role === 'locksmith') {
    base['history'] = st.history.map((a) => ({ ...a, digits: a.digits.slice() }));
    base['attemptsLeft'] = st.attemptsLeft;
    base['lockedUntilMs'] = st.lockedUntilMs;
  }
  if (ctx.revealed) base['secret'] = { code: par.code };
  return base;
}

function legalActs(): PuzzleAct[] {
  // The space of combinations is too large to list; callers use `getActionFamilies`.
  return [];
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  const clues = alterableClues(p, exclude);
  const clue = clues[0];
  if (!clue) return null;
  const list = (clueByKind(p, 'safeProfile').value as Constraint[]).map((c) => ({ ...c }));
  const i = rng.int(0, list.length - 1);
  const c = list[i] as Constraint;
  const other = (d: number): number =>
    rng.pick([0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter((x) => x !== d));
  switch (c.k) {
    case 'digitAt':
    case 'contains':
    case 'notContains':
      c.d = other(c.d);
      break;
    case 'sum':
      c.s = Math.max(0, c.s + rng.pick([-3, -2, -1, 1, 2, 3]));
      break;
    case 'parity':
      c.even = !c.even;
      break;
    case 'greater': {
      const a = c.a;
      c.a = c.b;
      c.b = a;
      break;
    }
  }
  return { clueId: clue.id, value: list };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as SafeParams;
  const candidates = listCandidates(par.digits, par.repeats, par.constraints, 100_000);
  const attempts = simulateSolve(par.code, candidates);
  return { solvable: attempts <= par.maxAttempts, difficulty: attempts };
}

function undo(p: PuzzleInstance, u: { kind: string; data?: unknown }): void {
  if (u.kind !== 'popAttempt') return;
  const d = u.data as { prevLeft: number; prevLock: number };
  const st = state(p);
  st.history.pop();
  st.attemptsLeft = d.prevLeft;
  st.lockedUntilMs = d.prevLock;
}

function autoSolve(p: PuzzleInstance): void {
  const par = params(p);
  state(p).history.push({ digits: par.code.slice(), clicks: par.digits, hums: 0 });
}

export const safeModule: PuzzleModule = {
  type: 'safe',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  undo,
  autoSolve,
  solve,
};
